import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, ScrollView, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import IconButtonM from "@common/components/button/IconButtonM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import CorrectionPointCard from "./CorrectionPointCard"

/**
 * Contextual bottom panel for the structured-correction points — the
 * on-demand companion of the full-height copy view ("Énoncé + Rendu").
 *
 * Expands with a short animation when a point is selected (from a copy
 * bubble or the points badge) and shows ONE card at a time in a horizontal,
 * snapping carousel — no type chips, no vertical stack. The bar carries the
 * position ("2/4"), optional surface-specific actions (e.g. tutor feedback),
 * prev/next arrows and a close button that returns the copy to full height.
 *
 * Swiping the carousel or using the arrows reports the new point through
 * `onSelectPoint`, so the consuming surface keeps the copy's markers (and
 * page) in sync. Stays surface-agnostic: no tutor/student wording inside.
 *
 * On native only the visible card mounts its explication WebView (±0), so
 * the carousel never stacks heavy WebViews.
 *
 * @param {Array} points          the correction points
 * @param {number|null} selectedIndex the selected point's `index` (1-based);
 *        null collapses the panel
 * @param {Function} onSelectPoint (index) => void — carousel/arrows navigation
 * @param {Function} onClose      () => void — the ✕ button
 * @param {number} [height]       expanded height — the consumer caps it to a
 *        fraction of its pane so the copy always keeps the lion's share
 * @param {React.ReactNode} [headerAccessory] e.g. a beta info tooltip
 * @param {React.ReactNode} [actions] surface-specific bar content (feedback…)
 */

const DEFAULT_PANEL_HEIGHT = 270
const ANIMATION_MS = 220

const CorrectionPointsPanel = ({
    points,
    selectedIndex,
    onSelectPoint,
    onClose,
    height = DEFAULT_PANEL_HEIGHT,
    headerAccessory,
    actions,
}) => {
    const orderedPoints = useMemo(
        () =>
            [...(points ?? [])].sort(
                (a, b) => (a?.index ?? 0) - (b?.index ?? 0)
            ),
        [points]
    )

    const visible = selectedIndex != null
    const position = Math.max(
        0,
        orderedPoints.findIndex((p) => p?.index === selectedIndex)
    )

    const heightAnim = useRef(new Animated.Value(0)).current
    useEffect(() => {
        Animated.timing(heightAnim, {
            toValue: visible ? height : 0,
            duration: ANIMATION_MS,
            useNativeDriver: false, // height cannot use the native driver
        }).start()
    }, [visible, height])

    // Carousel ↔ selection sync. `programmaticScrollRef` avoids re-selecting
    // the point we just scrolled to (momentum-end fires after scrollTo too).
    const [panelWidth, setPanelWidth] = useState(0)
    const scrollRef = useRef(null)
    const programmaticScrollRef = useRef(false)

    useEffect(() => {
        if (!visible || !panelWidth) return
        programmaticScrollRef.current = true
        scrollRef.current?.scrollTo({
            x: position * panelWidth,
            animated: true,
        })
    }, [position, visible, panelWidth])

    const handleMomentumEnd = (e) => {
        if (programmaticScrollRef.current) {
            programmaticScrollRef.current = false
            return
        }
        if (!panelWidth) return
        const index = Math.round(e.nativeEvent.contentOffset.x / panelWidth)
        const point = orderedPoints[index]
        if (point && point.index !== selectedIndex) {
            onSelectPoint?.(point.index)
        }
    }

    const goTo = (offset) => {
        const next = orderedPoints[position + offset]
        if (next) onSelectPoint?.(next.index)
    }

    if (!orderedPoints.length) return null

    return (
        <Animated.View style={[styles.container, { height: heightAnim }]}>
            <View
                style={{ height }}
                onLayout={(e) => {
                    const { width } = e.nativeEvent.layout
                    if (width > 0 && width !== panelWidth) setPanelWidth(width)
                }}
            >
                <View style={styles.bar}>
                    <TextM fontWeight="Bold" fontSize={13} color={theme.colors.grey}>
                        {position + 1}/{orderedPoints.length}
                    </TextM>
                    {headerAccessory ?? null}

                    <View style={styles.actions}>{actions ?? null}</View>

                    <IconButtonM
                        size={22}
                        icon="chevron-left"
                        iconColor={theme.colors.primary}
                        disabled={position === 0}
                        onPress={() => goTo(-1)}
                    />
                    <IconButtonM
                        size={22}
                        icon="chevron-right"
                        iconColor={theme.colors.primary}
                        disabled={position === orderedPoints.length - 1}
                        onPress={() => goTo(1)}
                    />
                    <IconButtonM
                        size={22}
                        icon="times"
                        iconColor={theme.colors.primary}
                        onPress={onClose}
                    />
                </View>

                {panelWidth > 0 && (
                    <ScrollView
                        ref={scrollRef}
                        horizontal
                        pagingEnabled
                        showsHorizontalScrollIndicator={false}
                        snapToInterval={panelWidth}
                        decelerationRate="fast"
                        contentOffset={{ x: position * panelWidth, y: 0 }}
                        onMomentumScrollEnd={handleMomentumEnd}
                        style={{ flex: 1 }}
                    >
                        {orderedPoints.map((point, i) => (
                            <View
                                key={point?.index ?? i}
                                style={[styles.page, { width: panelWidth }]}
                            >
                                <CorrectionPointCard
                                    point={point}
                                    explicationMode="fill"
                                    // ≤1 explication WebView mounted at a time
                                    // on native; web mounts them all (cheap).
                                    mountExplication={IS_WEB || i === position}
                                />
                            </View>
                        ))}
                    </ScrollView>
                )}
            </View>
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    container: {
        overflow: "hidden",
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    bar: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingLeft: 12,
        paddingRight: 4,
        paddingVertical: 2,
    },
    actions: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 8,
    },
    page: {
        paddingHorizontal: 12,
        paddingBottom: 10,
    },
    pageCardIdle: {
        opacity: 0.9,
    },
})

export default CorrectionPointsPanel
